/**
 * The persistent app shell: painted chrome over a live 3D scene + a content outlet that swaps
 * per route.
 *
 * Mounted once for the whole group of authenticated meta routes. `main.ts` creates it the
 * first time navigation lands on one of them, and disposes it only when navigation leaves the
 * group entirely (e.g. signing out to `/login`) — moving between `/play`, `/loadout`, `/shop`
 * and so on calls `setRoute`, which swaps only the outlet's contents. The chrome and the 3D
 * backdrop are created once and never rebuilt for the life of the session.
 *
 * The chrome is painted, not drawn (the painted kit, `styles/kit.css`, 2026-10-12 — "no flat
 * surface anywhere"):
 *   - the top bar is one `kit-plate` strip: the route tabs (`kit-tab` with the 3D route icon,
 *     the current one lit), then the balances as `kit-chip` slots with the coin/gem sitting over
 *     the left end and the green `add` key at the right, the level, and the sign-out key;
 *   - the phone's bottom bar is a `kit-plate` strip of `kit-plate` keys with big icons, the
 *     current one wearing `kit-selected`;
 *   - every notification mark is a `kit-badge`.
 * History worth keeping: §2b (2026-08-21) removed the chrome bar because a CSS-drawn strip read
 * as "web app with a navbar"; a strip of generated, 3D-rendered metal is a different object, and
 * the owner asked for it back (2026-10-12). The wordmark, the identity block (the player's card
 * and title, 2026-09-17) and the door-glyph sign-out (2026-09-07) are earlier reversals of that
 * same §2b and stay.
 *
 * The two nav surfaces are allowed to differ in arrangement, not reachability. The top row
 * (>=768px) is still the element `shell-check.mjs` and the unit tests know as `aside` and lists
 * every route. The bottom bar (<768px) keeps four primary routes plus More, because seven do
 * not fit a 375px bar at 44px targets. Every route keeps its own URL regardless of surface.
 *
 * z-index ladder (documented so route files can slot in without archaeology):
 *   0   page background (`body::before` in app.css)
 *   5   the 3D backdrop (`#menu-backdrop`, pointer-events: none)
 *   6   the centre content scrim (route-toggled: transparent on `/play`, dark elsewhere)
 *   10  `main` / the outlet — every route's own content
 *   15  a route's own fixed edge controls (`play-route.ts`'s PLAY stack)
 *   20  the painted top bar
 *   30  the mobile bottom tab bar
 *   50  modals (`openModal`)
 */
import type { AuthUser } from '../../core/state/auth-store';
import { t } from '../i18n';
import { META_ROUTES, resolveMetaPath } from './routes';
import { routeMountFor } from './routes/route-table';
import { countUp, revealOnMount } from './fx';
import { navBadgesStore } from './nav-badges-store';
import { button, icon, type IconName, openModal, plateChrome, plateContent } from './ui';
import { menuClick, playSample } from './menu-sfx';
import { mountMenuMusic } from './menu-music';
import { playerProgressStore, refreshPlayerProgress } from './player-progress-store';
import { playerCardPlate } from './player-card-plate';
import { NO_CARD } from '../../../../shared/src/player-cards.js';

export interface ShellHandle {
  /** Swaps the outlet's content to match `path`. Resolves unknown/`/` paths itself. */
  setRoute(path: string): void;
  /** Shows/hides the red notification badge on a nav entry (both surfaces). Default off.
   * The affordance exists ahead of its signals — quests/daily hooks wire in later. */
  setNavDot(path: string, on: boolean): void;
  dispose(): void;
}

/** The four routes a player checks every session — everything else lives behind `More`. */
const MOBILE_PRIMARY_PATHS = ['/play', '/loadout', '/shop', '/career'];

/**
 * A nav entry. Not `button()` from ui.ts: those are action keys, and a nav entry is a tab (top
 * row) or a plate key (bottom bar) whose lit state `setActiveNav` toggles through `data-active`
 * and `kit-selected` rather than a class rewrite.
 *
 * `[data-dot]` is a zero-size anchor holding a `kit-badge`: the badge's own `display` would
 * fight a `hidden` toggled on the same element, so the anchor carries the toggle and the badge
 * the paint. The "!" is a pseudo-element, which keeps it out of the button's `textContent` —
 * the tests, and a screen reader's name for the control, are the label alone.
 */
function navTab(
  label: string,
  onClick: () => void,
  art: IconName,
  layout: 'side' | 'bottom' = 'side',
): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.setAttribute('aria-label', label);
  // An idle top tab is icon-only, so hover names it (enhancement only — the icon and the
  // aria-label carry it without one).
  if (layout === 'side') el.title = label;

  const glyph = icon(art, { size: layout === 'bottom' ? 30 : 28, className: '[filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' });
  const text = document.createElement('span');
  text.dataset.navLabel = '';
  text.textContent = label;

  if (layout === 'bottom') {
    // A pressable steel key: the glyph large, the word under it in game lettering.
    el.className =
      'kit-plate kit-plate-hit [--kp-rim:10px] [--kp-pad:5px_2px_3px] flex-1 min-w-[44px] min-h-[52px] ' +
      'flex flex-col items-center justify-center gap-0.5 bg-transparent';
    text.className = 'kit-label font-heading uppercase text-[10px] leading-none truncate max-w-full';
  } else {
    // The painted tab carries the 3D icon only, at every width; the screen's own title names the
    // route right under the bar. Showing the current tab's word "from 1800px" still ran SETTINGS
    // under the identity plate at 1900 (2026-10-14) — the fourth width threshold to fail here,
    // so the word is gone rather than re-tuned. Seven
    // labelled tabs plus the identity and balances do not share one line at any width — the
    // 2K composition zoom (app.css) alone made the row ~1450px of layout (2026-10-12). The icon
    // is findable by shape; the word is the aria-label and the hover title.
    el.className = 'kit-tab [--kh:44px] font-heading font-bold uppercase text-[13px] tracking-[0.06em] whitespace-nowrap';
    text.className = 'hidden leading-none';
  }
  el.append(glyph, text);

  const dot = document.createElement('span');
  dot.dataset.dot = '';
  dot.setAttribute('aria-hidden', 'true');
  dot.className = 'hidden absolute top-2 right-2 w-0 h-0';
  const badgeMark = document.createElement('span');
  badgeMark.className = "kit-badge after:content-['!']";
  dot.append(badgeMark);
  el.append(dot);

  el.addEventListener('click', () => {
    // The nav clicks like an instrument, quietly — same synthesized gesture the play screen's
    // stack uses, volume-governed by the player's own master slider (menu-sfx.ts).
    menuClick();
    onClick();
  });
  return el;
}

/**
 * A balance readout: the painted `kit-chip` slot with the coin or gem sitting over its left end
 * and, for the two currencies, the green `add` key over its right — inside a real 44px button
 * (the chip is 34px: the button is the tap target, the chip is the picture).
 */
function balanceChip(
  art: IconName,
  figure: HTMLElement,
  onClick: () => void,
  opts: { add: boolean },
): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className =
    'h-11 min-w-[44px] inline-flex items-center cursor-pointer touch-manipulation bg-transparent border-0 p-0 ' +
    'transition-[filter] duration-150 hover:brightness-125 focus-visible:outline focus-visible:outline-2 ' +
    'focus-visible:outline-cyan/70 focus-visible:outline-offset-2';
  const chip = document.createElement('span');
  chip.className = 'kit-chip [--kh:30px] sm:[--kh:34px] font-heading text-[13px] sm:text-[16px]';
  const glyph = icon(art, { size: 30, className: 'absolute -left-2.5 top-1/2 -translate-y-1/2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' });
  // The margins clear the figure of the icons that overlap the slot's ends.
  figure.className = `kit-label tabular-nums ml-3 ${opts.add ? 'mr-1 sm:mr-3' : 'mr-2'}`;
  chip.append(glyph, figure);
  if (opts.add) {
    chip.append(icon('add', { size: 28, className: 'absolute -right-2.5 top-1/2 -translate-y-1/2 max-sm:hidden [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }));
  }
  el.append(chip);
  el.addEventListener('click', onClick);
  return el;
}

export function mountShell(root: HTMLElement, user: AuthUser, navigate: (to: string) => void, onLogout: () => void): ShellHandle {
  root.innerHTML = '';

  const shellRoot = document.createElement('div');
  shellRoot.className = 'min-h-dvh flex flex-col bg-bg text-text';

  // ══════════════════════════════════════════════════════════════════════════════════════════
  // THE 3D BACKDROP — the shell's floor, persistent behind every route (superseded-§2 note in
  // design/gdd/meta-visual-language.md). Vignette (the always-there fallback), poster image
  // (paints before the chunk lands; the whole picture on mobile/reduced-motion), the live
  // scene's mount, then a text scrim. Mounted once with the chrome, disposed only in
  // `dispose()` — navigating between routes never tears the canvas down.
  //
  // The live scene is loaded with a **dynamic** `import()`, never a static one, so an
  // unauthenticated visitor never downloads Three.js (`grep -c "three" dist/assets/index-*.js`
  // stays `0`). Gated on viewport width *and* `prefers-reduced-motion` before the import ever
  // fires — the fix for a phone is never asking for the chunk, not hiding it.
  // ══════════════════════════════════════════════════════════════════════════════════════════
  const backdrop = document.createElement('div');
  backdrop.id = 'menu-backdrop';
  backdrop.dataset.slot = 'menu-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.className = 'fixed inset-0 z-[5] overflow-hidden pointer-events-none';
  backdrop.innerHTML = `
    <div class="absolute inset-0" style="background:
      radial-gradient(ellipse 70% 55% at 50% 68%, rgba(23,224,201,0.10), transparent 60%),
      radial-gradient(ellipse 90% 70% at 50% 100%, rgba(0,0,0,0.75), transparent 60%),
      linear-gradient(180deg, var(--color-bg) 0%, #0d0f13 55%, var(--color-bg) 100%);"></div>
    <img src="/game/lobby-poster.jpg" alt=""
      class="absolute inset-0 w-full h-full object-cover" />
  `;
  const sceneMount = document.createElement('div');
  sceneMount.dataset.slot = 'menu-backdrop-scene';
  sceneMount.className = 'absolute inset-0';
  backdrop.appendChild(sceneMount);

  // The text scrim — painted after the scene mount so it sits on top of whatever the canvas
  // renders: a top band behind the floating nav, a strong bottom-right patch behind `/play`'s
  // action stack, a lighter bottom-left wash behind "Community". Structural legibility: dark
  // and readable regardless of what the scene's grading does. Pure decoration, never a hit
  // target. With the chrome bar gone this top band is doing MORE work than before — it is the
  // only thing under the floating nav besides each label's own text-shadow.
  const sceneScrim = document.createElement('div');
  sceneScrim.className = 'absolute inset-0';
  sceneScrim.style.background = [
    'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 16%)',
    'radial-gradient(ellipse 640px 460px at 100% 100%, rgba(0,0,0,0.7), transparent 65%)',
    'radial-gradient(ellipse 380px 300px at 0% 100%, rgba(0,0,0,0.4), transparent 65%)',
  ].join(', ');
  backdrop.appendChild(sceneScrim);

  let disposeLobbyScene: (() => void) | null = null;
  let lobbyScene: import('../lobby-scene.js').LobbySceneHandle | null = null;
  let backdropUnmounted = false;

  // `768px` matches this file's own mobile/desktop split. Guarded rather than called bare:
  // jsdom (the unit tests) may lack `matchMedia` entirely, and the safe answer there is the
  // poster, not a throw that takes the shell down with it.
  const matches = (query: string): boolean =>
    typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const canAffordLiveScene = matches('(min-width: 768px)') && !matches('(prefers-reduced-motion: reduce)');

  /**
   * How many times the backdrop may rebuild itself after losing its WebGL context.
   *
   * A browser gives one page a limited number of contexts (Chrome: 16) and takes the OLDEST
   * when it needs another — and the oldest is always this one, mounted before any preview.
   * The leak that got it there is fixed at the source (`forceContextLoss()` where the previews
   * are disposed), but a context can still go on a GPU reset or when another tab is greedy,
   * and what a lost one draws is a half-built room rather than nothing ("не тот фон лобби",
   * QA item 27). Capped rather than unlimited: if contexts are genuinely exhausted, rebuilding
   * forever would be a loop nobody can see.
   */
  const BACKDROP_REBUILDS = 2;
  let backdropRebuilds = 0;

  if (canAffordLiveScene) {
    const mountBackdrop = (mountLobbyScene: typeof import('../lobby-scene.js').mountLobbyScene): void => {
      if (backdropUnmounted) return;
      const handle = mountLobbyScene(sceneMount);
      lobbyScene = handle;
      disposeLobbyScene = () => {
        lobbyScene = null;
        handle.dispose();
      };
      const canvas = sceneMount.querySelector('canvas');
      canvas?.addEventListener(
        'webglcontextlost',
        (event) => {
          // Without `preventDefault()` the browser never offers the context back at all. The
          // scene is rebuilt from scratch either way: three.js keeps no state that survives.
          event.preventDefault();
          if (backdropUnmounted || backdropRebuilds >= BACKDROP_REBUILDS) return;
          backdropRebuilds++;
          disposeLobbyScene?.();
          disposeLobbyScene = null;
          sceneMount.innerHTML = '';
          // Next frame: the browser has not finished tearing the old context down inside the
          // event handler, and a renderer built there gets another dead one.
          requestAnimationFrame(() => mountBackdrop(mountLobbyScene));
        },
        { once: true },
      );
    };
    void import('../lobby-scene.js')
      .then(({ mountLobbyScene }) => mountBackdrop(mountLobbyScene))
      .catch((error: unknown) => {
        // The poster is still there underneath — a failed chunk load must never blank the
        // backdrop, only leave it static instead of live.
        console.warn('[shell] 3D backdrop failed to load', error);
      });
  }

  // The centre scrim: content routes lay tables and cards over the live scene, and glass panels
  // alone don't guarantee a readable column — this darkens the middle of the viewport on every
  // route EXCEPT `/play`, whose centre belongs to the character. Toggled by opacity in
  // `setRoute` (one property, transitionable) rather than mounted/unmounted per route.
  const contentScrim = document.createElement('div');
  contentScrim.dataset.slot = 'content-scrim';
  contentScrim.setAttribute('aria-hidden', 'true');
  contentScrim.className = 'fixed inset-0 z-[6] pointer-events-none transition-opacity duration-300';
  // A near-solid wash, not a soft ellipse. The previous gradient fell to 0.12 alpha at the
  // edges, which on a wide screen left the lobby's own soldier and crates fully lit beside the
  // content — a storefront's tiles floating over a photograph of a warehouse, reported as
  // "everything looks creepy and crooked". A content route is a PAGE: it gets a ground to sit
  // on. The radial stays on top of the flat wash, for depth rather than for legibility, and
  // `/play` still turns the whole thing off so the character keeps the stage.
  contentScrim.style.background = [
    'radial-gradient(ellipse 70% 80% at 50% 40%, rgba(5,6,8,0.55), transparent 70%)',
    'linear-gradient(180deg, rgba(5,6,8,0.90) 0%, rgba(5,6,8,0.93) 45%, rgba(5,6,8,0.96) 100%)',
  ].join(', ');
  contentScrim.style.opacity = '0';

  // ── The painted top bar: ONE `kit-plate` strip holding the route tabs, the balances and the
  // way back to the lobby. ─────────────────────────────────────────────────────────────────

  // The tab row: every route, top-left, ≥768px only. Kept an `<aside>` — the element
  // shell-check.mjs and the unit tests find by tag.
  //
  // It is the `flex-1 min-w-0` child of `topBar` (below), laid out in ONE flex row with the
  // balances. An earlier design positioned the pieces independently and subtracted a right-hand
  // reserve from the nav — 460px, then 560px, then a ResizeObserver — and SETTINGS ran under the
  // balances three times, because the reserve described a box another element owned. One row,
  // no arithmetic. From 1440px it never wraps (a two-line tab row is broken); below it may,
  // since nothing reasonable fits tabs and balances in 1000px on one line.
  //
  // `pointer-events-none` on the bar with `auto` on its children keeps the empty stretch between
  // the last tab and the balances from swallowing clicks meant for the scene.
  const sideRail = document.createElement('aside');
  sideRail.className =
    // `overflow-x-clip`: if the row ever runs out of width, the last tabs are cut at the row's
    // edge instead of drawing UNDER the balances, where they looked clickable and were not.
    'hidden md:flex flex-1 min-w-0 flex-wrap min-[1440px]:flex-nowrap overflow-x-clip items-center pointer-events-none [&>*]:pointer-events-auto ' +
    'gap-x-1.5 gap-y-1 min-[1440px]:gap-x-[clamp(2px,0.4vw,8px)]';

  // The wordmark: the game's NAME ahead of the tabs, from 2560px only. At 2400px — also where
  // `app.css` steps the composition zoom 1.18 -> 1.32, narrowing the row in layout terms — the
  // mark's width landed on the one width the row could least spare it (measured 2026-09-21,
  // `nav-fit.mjs`). The stage is capped at 1680px of layout, so no wider monitor grows the row.
  // Authored at 30px, which the zoom paints at 39.6px.
  const wordmark = document.createElement('span');
  wordmark.dataset.slot = 'wordmark';
  wordmark.setAttribute('aria-hidden', 'true');
  wordmark.className =
    'hidden min-[2560px]:block kit-title font-heading font-extrabold italic uppercase text-[30px] leading-none ' +
    'select-none pr-5 mr-1';
  wordmark.textContent = 'Black Line';
  sideRail.append(wordmark);

  // Top-right: identity, balances, level, sign-out. In-flow inside `topBar`, pushed to the right
  // edge by `ml-auto`; `shrink-0` from `md` so the tabs, not the balances, give way. Below `md`
  // there is no rail, so the cluster may wrap instead of running off a 320px screen.
  const cluster = document.createElement('div');
  cluster.dataset.slot = 'currency-cluster';
  cluster.className =
    // `gap-x-6` from `md`: the green add key overhangs each balance's right end by 10px, and at
    // 16px it touched the next chip's coin (2026-10-12). One row on a phone, compacted.
    'ml-auto md:shrink-0 max-md:min-w-0 max-md:justify-end flex flex-nowrap items-center gap-x-5 sm:gap-x-4 md:gap-x-6 pointer-events-auto md:pl-4';

  /**
   * The last balance and level this cluster painted, so a change can be *counted to*.
   *
   * The subscription used to rewrite `cluster.innerHTML` wholesale on every progress push,
   * which meant a 5,000-credit purchase and a 40-credit daily claim looked identical: a figure
   * silently holding a different value the next time you glanced at it. The count is what
   * carries the delta, and the delta is the whole reason the number moved.
   */
  let shownCredits: number | null = null;
  let shownCoins: number | null = null;
  let shownLevel: number | null = null;
  let shownCard: number | null = null;

  // ── Identity: the player's own name, in whatever card they wear, title beneath. ────────────
  //
  // A persistent fact the player paid for needs a persistent place to be seen (4th §2b
  // reversal, 2026-09-17). Cut into the bar as a `kit-well`, capped and truncated: an unbounded
  // string in this row is how 390px measured negative before. Hidden below 1800px: at 1440, on
  // an overlay route (LOBBY key in the row too) it pushed QUESTS and SETTINGS under the balances
  // (2026-10-12). The dossier and Career show the name anyway.
  const identity = document.createElement('div');
  identity.dataset.slot = 'identity';
  identity.className =
    'kit-well flex flex-col justify-center min-w-0 shrink gap-0.5 max-w-[170px] px-3 py-1 max-[1799px]:hidden pointer-events-auto';

  const identityName = document.createElement('span');
  identityName.dataset.slot = 'identity-name';
  identityName.className = 'kit-label font-heading uppercase text-[13px] truncate leading-tight';
  identityName.textContent = user.username ?? user.email;

  // Omitted, not blanked, when nothing is worn — the same rule Career's own title row keeps: an
  // empty line reads as a loading state on a screen that already loaded.
  const identityTitle = document.createElement('div');
  identityTitle.dataset.slot = 'identity-title';

  // `NO_CARD` (Standard Issue) draws unadorned — see `player-card-plate.ts` — so an account
  // that owns no card costs this row nothing more than its name.
  let identityPlate = playerCardPlate(NO_CARD, identityName);
  identity.append(identityPlate, identityTitle);

  const creditsValue = document.createElement('span');
  creditsValue.dataset.slot = 'credits-value';
  const coinsValue = document.createElement('span');
  coinsValue.dataset.slot = 'coins-value';
  const levelValue = document.createElement('span');
  levelValue.dataset.slot = 'level-value';

  // Both balances are real buttons: the balance opens the Shop (where credits get spent and
  // gems get topped up), the level opens Career (where it is tracked).
  const credits = balanceChip('credits', creditsValue, () => {
    playSample('select');
    navigate('/shop#coins');
  }, { add: true });
  credits.title = t('common.credits');
  credits.setAttribute('aria-label', t('shell.openShop'));

  // GEMS — the second balance, and the reason "the wallet doesn't update after buying gems"
  // was a real bug rather than a stale cache: a top-up credits `premiumBalance`, the header
  // only ever read `softBalance`. Both are drawn because the shell spends both — credits buy
  // skins, titles and weapons, gems buy weapon finishes and loadout slots.
  const coins = balanceChip('premium', coinsValue, () => {
    playSample('select');
    navigate('/shop#coins');
  }, { add: true });
  coins.dataset.slot = 'coins';
  coins.title = t('common.gems');
  coins.setAttribute('aria-label', t('shell.openShopGems'));

  // The level: the rank chevrons over the left end of the slot, "LVL" then the figure — "LVL 68"
  // is one readout, "68 LV" is a number with a unit stuck on the end. No add key: a level is
  // earned, not bought.
  const levelPrefix = document.createElement('span');
  levelPrefix.className = 'mr-1.5';
  levelPrefix.textContent = t('common.levelShort');
  const levelFigure = document.createElement('span');
  levelFigure.append(levelPrefix, levelValue);
  const level = balanceChip('rank', levelFigure, () => {
    playSample('select');
    navigate('/career');
  }, { add: false });
  // Not on a phone: three slots plus their overhanging coin and chevrons ran into each other at
  // 375px (2026-10-12). Career and the dossier show the level there.
  level.classList.add('max-sm:hidden');
  level.title = t('common.level');
  level.setAttribute('aria-label', t('shell.openCareer'));

  // Sign out — an exit reachable from the header, not only buried in Settings (which keeps its
  // own copy: `signOutButton` in `settings-route.ts`). A square steel key with the door glyph;
  // `max-sm:hidden` so a phone's row keeps the balances (Settings has it there).
  const signOut = document.createElement('button');
  signOut.type = 'button';
  plateChrome(signOut, icon('logout', { size: 26 }), { variant: 'secondary', size: 'md', className: 'plate-icon max-sm:hidden' });
  signOut.dataset.slot = 'sign-out';
  signOut.title = t('shell.signOutTitle');
  signOut.setAttribute('aria-label', t('shell.signOutTitle'));
  signOut.addEventListener('click', () => {
    playSample('close');
    onLogout();
  });
  cluster.append(identity, credits, coins, level, signOut);

  const unsubscribeProgress = playerProgressStore.subscribe((progress) => {
    if (!progress) {
      creditsValue.textContent = '—';
      coinsValue.textContent = '—';
      levelValue.textContent = '—';
      return;
    }
    // First paint assigns; every later one counts. Counting up from nothing on arrival would
    // make every page load look like a payout.
    countUp(creditsValue, shownCredits ?? progress.softBalance, progress.softBalance);
    countUp(coinsValue, shownCoins ?? progress.premiumBalance, progress.premiumBalance);
    countUp(levelValue, shownLevel ?? progress.level, progress.level, {
      format: (value) => String(Math.round(value)),
    });
    // A level that went UP is the one progress event with no other surface anywhere in the
    // shell — there is no level-up screen, and the arena navigates away before one could
    // exist. The pulse is the whole of it, and it costs nothing.
    if (shownLevel !== null && progress.level > shownLevel) {
      level.animate(
        [
          { transform: 'scale(1)', filter: 'brightness(1)' },
          { transform: 'scale(1.35)', filter: 'brightness(2.2)', offset: 0.3 },
          { transform: 'scale(1)', filter: 'brightness(1)' },
        ],
        { duration: 900, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    }
    shownCredits = progress.softBalance;
    shownCoins = progress.premiumBalance;
    shownLevel = progress.level;

    // The card only redraws when it actually changed — balances move far more often than a
    // card does, and rebuilding the plate on every count-to would be needless churn on an
    // element nothing about a currency has anything to do with.
    if (progress.equippedCard !== shownCard) {
      const nextPlate = playerCardPlate(progress.equippedCard, identityName);
      identityPlate.replaceWith(nextPlate);
      identityPlate = nextPlate;
      shownCard = progress.equippedCard;
    }
    if (progress.equippedTitle) {
      identityTitle.className = 'text-[10px] font-heading uppercase tracking-wide text-gold truncate leading-tight';
      identityTitle.textContent = progress.equippedTitle;
    } else {
      identityTitle.className = '';
      identityTitle.textContent = '';
    }
  });
  void refreshPlayerProgress();

  // The overlay's way back. `closeX` is the positioned WRAPPER (`setRoute` toggles its
  // `md:flex`/`md:hidden`, so its base class carries `hidden` only); the control inside is a
  // painted key labelled LOBBY with a back arrow rather than the red enamel ×: a red × in the
  // corner is the universal "quit" glyph, and it read as leaving the game.
  const closeX = document.createElement('div');
  closeX.dataset.slot = 'overlay-close';
  // Inside `closeSlot`, which hangs UNDER the right end of `topBar` on /play and ends the row on
  // overlay routes. `setRoute` toggles `md:flex`/`md:hidden` here.
  closeX.className = 'hidden items-center pointer-events-auto';
  const backToLobby = document.createElement('button');
  backToLobby.type = 'button';
  backToLobby.setAttribute('aria-label', t('nav.backToLobby'));
  backToLobby.title = t('nav.backToLobby');
  const backLabel = document.createElement('span');
  backLabel.className = 'inline-flex items-center gap-2 text-[14px] tracking-[0.1em]';
  // The word goes in by `textContent`: a translated string is still data.
  const backWord = document.createElement('span');
  backWord.textContent = t('nav.lobby');
  backLabel.append(icon('prev', { size: 22 }), backWord);
  plateChrome(backToLobby, backLabel, { variant: 'secondary', size: 'md' });
  // On the wrapper, not the key: a click on the key bubbles here, and the wrapper is the
  // element `setRoute` and the tests address.
  closeX.addEventListener('click', () => {
    playSample('close');
    navigate('/play');
  });
  closeX.append(backToLobby);

  // ── Bottom tab bar (mobile, <768px): a painted strip of plate keys. ──────────────────────────
  // Mobile-first: the meta screens are not overridden by a desktop reference.
  const bottomNav = document.createElement('nav');
  bottomNav.dataset.slot = 'bottom-nav';
  bottomNav.setAttribute('aria-label', t('shell.primary'));
  bottomNav.className =
    'md:hidden fixed inset-x-0 bottom-0 z-30 flex gap-1 kit-plate [--kp-rim:12px] ' +
    '[--kp-pad:10px_10px_calc(8px+env(safe-area-inset-bottom))]';

  // Each entry answers to one or more paths: an ordinary tab answers to exactly its own route;
  // `More` answers to any of the routes it hosts, so it lights up as "you are somewhere in
  // here" rather than never lighting up at all.
  const navEntries: { paths: string[]; el: HTMLButtonElement }[] = [];

  const secondaryRoutes = META_ROUTES.filter((route) => !MOBILE_PRIMARY_PATHS.includes(route.path));

  function openMoreSheet(): void {
    let closeSheet: () => void = () => {};
    // Each route is a big steel key with its 3D icon, dealt in. The wrapper takes the entrance:
    // `kit-deal` ends on `transform: none`, which would pin the key's own hover lift.
    const list = document.createElement('div');
    list.className = 'flex flex-col gap-3';
    secondaryRoutes.forEach((route, index) => {
      const slot = document.createElement('div');
      slot.className = 'kit-deal';
      slot.style.setProperty('--i', String(index));
      const entry = button(route.label, {
        variant: 'secondary',
        size: 'lg',
        className: 'w-full',
        onClick: () => {
          closeSheet();
          navigate(route.path);
        },
      });
      // The icon rides inside the plate's label node so it sits above the painted face, and the
      // sheet reads as the same nav the tab row does.
      plateContent(entry).prepend(icon(route.icon, { size: 32 }));
      if (route.path === '/quests' && navBadgesStore.get().quests) {
        const mark = document.createElement('span');
        mark.setAttribute('aria-hidden', 'true');
        mark.className = "kit-badge after:content-['!']";
        entry.append(mark);
      }
      slot.append(entry);
      list.append(slot);
    });
    closeSheet = openModal({
      title: t('nav.more'),
      content: [list],
      onClose: () => {},
    });
  }

  // ── Tab row: every route. ─────
  for (const route of META_ROUTES) {
    const sideBtn = navTab(route.label, () => navigate(route.path), route.icon);
    sideRail.append(sideBtn);
    navEntries.push({ paths: [route.path], el: sideBtn });
  }

  // ── Bottom bar: the four per-session/between-match routes, plus `More`. ──
  for (const path of MOBILE_PRIMARY_PATHS) {
    const route = META_ROUTES.find((r) => r.path === path)!;
    const bottomBtn = navTab(route.label, () => navigate(route.path), route.icon, 'bottom');
    bottomNav.append(bottomBtn);
    navEntries.push({ paths: [route.path], el: bottomBtn });
  }
  const moreBtn = navTab(t('nav.more'), openMoreSheet, 'add', 'bottom');
  bottomNav.append(moreBtn);
  navEntries.push({ paths: secondaryRoutes.map((r) => r.path), el: moreBtn });

  function setActiveNav(path: string): void {
    for (const entry of navEntries) {
      const active = entry.paths.includes(path);
      const onBottom = bottomNav.contains(entry.el);
      entry.el.dataset.active = String(active);
      entry.el.setAttribute('aria-current', active ? 'page' : 'false');
      // The lit state: the tab swaps to its glowing art through `data-active` (kit.css); the
      // bottom key wears the teal selection light.
      if (onBottom) entry.el.classList.toggle('kit-selected', active);
    }
  }

  function setNavDot(path: string, on: boolean): void {
    for (const entry of navEntries) {
      if (!entry.paths.includes(path)) continue;
      const dot = entry.el.querySelector<HTMLElement>('[data-dot]');
      if (!dot) continue;
      const wasHidden = dot.classList.contains('hidden');
      dot.classList.toggle('hidden', !on);
      // The moment a badge APPEARS it earns one attention beat — a single 700ms scale pulse,
      // never a loop of our own (the badge's own idle pop is slow and `motion-safe`). Only on
      // the off→on edge, and only for motion-tolerant users.
      if (on && wasHidden && typeof dot.animate === 'function' &&
          !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        dot.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(1.8)', offset: 0.35 },
            { transform: 'scale(1)' },
          ],
          { duration: 700, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        );
      }
    }
  }

  // The badges. `setNavDot` has existed since the chrome was built and had no callers outside
  // its own test; this is the wiring that was missing, not a new feature.
  const unsubscribeBadges = navBadgesStore.subscribe((badges) => {
    setNavDot('/quests', badges.quests);
    // The daily streak is collected from `/play`, so that is the tab that carries its badge.
    setNavDot('/play', badges.daily);
  });

  // ── Outlet ───────────────────────────────────────────────────────────────
  // On `/play` the outlet is an empty shell (the route floats its own fixed controls); on
  // every other route it is the OVERLAY's content region: a padded, centred column floating
  // over the scrimmed scene, cleared at the top so the floating nav never overlaps a screen
  // title. The menu route escapes via `position: fixed` rather than this file growing a
  // second layout mode — see `play-route.ts`.
  // `relative z-10`: the backdrop is a positioned z-5 element, so normal-flow content would
  // paint UNDER it — the outlet has to be lifted above the scene explicitly.
  //
  // The bar has TWO positions, and which one it is in is the whole difference between a HUD
  // floating over a 3D scene and a document's header. On `/play` it is `fixed`, floating over
  // the character. On every overlay route it becomes an ordinary block in `shellRoot`'s flex
  // column, ABOVE `main` — so it occupies real height, `main` (`flex-1 overflow-y-auto`) gets
  // exactly the space that is left, and no scroll position can ever put content under it.
  // Padding reserved for a fixed bar does not achieve that: it only pushes the overlap further
  // down the scroll, which is the bug ("the sticky navbar overlaps open pages and popups").
  //
  // It stays a `shellRoot` child in BOTH modes and never changes parent. Re-parenting it into
  // `main` also fixed the overlap, but moving a DOM node restarts every CSS animation and
  // transition under it, and the first nav click after a move then raced Playwright's
  // element-stability check — `shell:check` failed on a different route every run.
  // The bar is one painted `kit-plate` strip. Its padding is `--kp-pad` — a padding utility on
  // the same element would fight `kit-plate`'s own by stylesheet order, not class order.
  const TOP_BAR_BASE =
    'kit-plate [--kp-rim:12px] [--kp-pad:8px_14px] md:[--kp-pad:10px_22px] flex flex-nowrap items-center pointer-events-none';
  const TOP_BAR_FIXED = 'fixed top-3 left-[var(--stage-inset)] right-6 z-20';
  // Same horizontal insets as the fixed layer, so the single-line tab row has exactly the
  // width it is tuned for at 1440 and nothing reflows between the two modes.
  // `overflow-x-clip`: a fixed element's overflow never grew the document, so at 375px the bar
  // could quietly run wider than the screen. In flow that overflow becomes real horizontal page
  // scroll on every overlay route (`shell:check` caught all eight). Clipping keeps exactly the
  // old behaviour — the same pixels stay off-screen — without a scrollbar. `clip` and not
  // `hidden` so nothing establishes a scroll container. `-x` only: `-y` is visible.
  const TOP_BAR_FLOW =
    'relative z-20 shrink-0 max-w-full overflow-x-clip mt-3 mb-2 ml-[var(--stage-inset)] mr-6';
  const CLOSE_SLOT_BASE = 'hidden md:flex pointer-events-none';
  const CLOSE_SLOT_FIXED = 'absolute right-0 top-full mt-2';
  // In flow there is nothing to hang under: the key becomes the last child of the row, so it
  // still reads at the bar's right end without overlapping the first line of the route.
  const CLOSE_SLOT_FLOW = 'shrink-0 self-center ml-3';
  const MAIN_BASE =
    'relative z-10 flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 ' +
    'pb-[calc(84px+env(safe-area-inset-bottom))] md:pb-10';
  // Menu: clear the fixed bar (~68px tall at top-3). Overlay: the bar is in flow and brings its
  // own spacing, so the reserve would double-space every screen.
  const MAIN_PAD_MENU = 'pt-6 md:pt-[96px]';
  const MAIN_PAD_OVERLAY = 'pt-2 md:pt-3';

  const main = document.createElement('main');
  main.className = `${MAIN_BASE} ${MAIN_PAD_MENU}`;
  const outlet = document.createElement('div');
  // 1400, not 1100. Every reference sheet in `design/gdd/reference/` is drawn full-bleed at
  // 1920, and the store sheet runs five cards across; at 1100 a 1440px screen gave each of five
  // operator cards about 200px, which is where the clipped names and the price colliding with
  // its own hint came from. Still capped — an unbounded column turns a grid into a spreadsheet
  // on an ultrawide.
  outlet.className = 'max-w-[1400px] mx-auto w-full';
  main.append(outlet);

  // ── The top bar: tabs, balances and the LOBBY key in ONE flex row. ─────────────────────────
  //
  // `fixed` once, on the bar, from the stage's left inset to the VIEWPORT's right edge (the
  // balances go as far right as they can — that is what buys the tabs their single line); the
  // tab row and the cluster are ordinary flex children. The bar itself ignores the pointer (it
  // covers the whole top strip of the scene) and each piece opts back in. `closeSlot` hangs
  // under the bar's right end, so the LOBBY key costs the row nothing and the balances never
  // shift between menu and overlay routes.
  const topBar = document.createElement('div');
  topBar.dataset.slot = 'top-bar';
  topBar.className = `${TOP_BAR_BASE} ${TOP_BAR_FIXED}`;
  const closeSlot = document.createElement('div');
  closeSlot.dataset.slot = 'overlay-close-slot';
  closeSlot.className = `${CLOSE_SLOT_BASE} ${CLOSE_SLOT_FIXED}`;
  closeSlot.append(closeX);
  topBar.append(sideRail, cluster, closeSlot);

  shellRoot.append(backdrop, contentScrim, topBar, main, bottomNav);
  root.append(shellRoot);

  // The ambient bed — armed by the first gesture, governed live by the Settings toggle,
  // disposed with the shell (the arena must never inherit menu music).
  const menuMusic = mountMenuMusic();

  let routeTeardown: (() => void) | null = null;
  let activePath: string | null = null;

  // Escape closes the current overlay screen back to the menu — unless a modal is open, in
  // which case the modal's own Escape handler owns the key (both listen on `document`;
  // checking for an open dialog here is what keeps one keypress from closing both layers).
  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    if (activePath === null || activePath === '/play') return;
    if (document.querySelector('[role="dialog"]')) return;
    navigate('/play');
  }
  document.addEventListener('keydown', onKeydown);

  function setRoute(path: string): void {
    const resolved = resolveMetaPath(path);
    if (resolved === activePath) return;
    // The page turn — only when a route actually changes hands, never on the first mount
    // (arriving at the menu is not a transition, and the sample would race the autoplay gate).
    if (activePath !== null) playSample('transition');
    routeTeardown?.();
    outlet.innerHTML = '';
    activePath = resolved;
    setActiveNav(resolved);
    // `/play`'s centre belongs to the character; every other route is an overlay: darkened
    // centre, red X in the corner.
    const isMenu = resolved === '/play';
    contentScrim.style.opacity = isMenu ? '0' : '1';
    // Back at the menu: the figure re-reads what is equipped, because the Armoury, Inventory
    // and Loadout screens change it while this scene keeps standing behind them.
    if (isMenu) lobbyScene?.refresh();
    // Re-seat the top bar for this route's layer (see the constants above). A class toggle plus
    // one `insertBefore`, never a rebuild — the bar owns live state (balances, nav badges, the
    // scene-audio gesture) that must survive a route change untouched.
    topBar.className = `${TOP_BAR_BASE} ${isMenu ? TOP_BAR_FIXED : TOP_BAR_FLOW}`;
    closeSlot.className = `${CLOSE_SLOT_BASE} ${isMenu ? CLOSE_SLOT_FIXED : CLOSE_SLOT_FLOW}`;
    main.className = `${MAIN_BASE} ${isMenu ? MAIN_PAD_MENU : MAIN_PAD_OVERLAY}`;
    // A route change is a new page: start it at the top, rather than inheriting the previous
    // screen's scroll offset.
    main.scrollTop = 0;
    // One entrance per route change, on the outlet's own children, after the route has painted
    // them. Not an entrance declared per component: nine screens each animating themselves in
    // is how motion stops reading as composed and starts reading as decoration someone added
    // section by section. `/play` is excluded — its content is fixed furniture around a live
    // 3D scene, and sliding the corners of the menu on every visit would be noise.
    if (!isMenu) requestAnimationFrame(() => revealOnMount(outlet));
    closeX.classList.toggle('md:flex', !isMenu);
    closeX.classList.toggle('md:hidden', isMenu);
    routeTeardown = routeMountFor(resolved)(outlet, { user, navigate, onLogout });
  }

  function dispose(): void {
    menuMusic.dispose();
    backdropUnmounted = true;
    disposeLobbyScene?.();
    disposeLobbyScene = null;
    routeTeardown?.();
    routeTeardown = null;
    unsubscribeProgress();
    unsubscribeBadges();
    document.removeEventListener('keydown', onKeydown);
    root.innerHTML = '';
  }

  return { setRoute, setNavDot, dispose };
}
